import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ToolSidebar from '../../utils/ToolSideBar';
import { createTicket, fetchWorkboard } from '../../../services/workboardApi';
import '../../../css/superuser/StaffDirectoryPage.css';
import '../../../css/staff/InternalWorkspace.css';

function TicketCard({ ticket }) {
  return (
    <article className={`workboard-ticket type-${ticket.type_color || ticket.ticket_type}`}>
      <div className="workboard-ticket-top">
        <strong>{ticket.ticket_key}</strong>
        <span className={`workboard-priority priority-${ticket.priority}`}>{ticket.priority_label}</span>
      </div>
      <h3>{ticket.title}</h3>
      <div className="workboard-badges">
        <span className={`workboard-type type-${ticket.type_color || ticket.ticket_type}`}>{ticket.ticket_type_label}</span>
        {ticket.requires_qa && <span>QA</span>}
        {ticket.cross_team_state !== 'none' && <span>Cross-Team</span>}
      </div>
      <dl>
        <div><dt>Project</dt><dd>{ticket.project?.name || 'Internal'}</dd></div>
        <div><dt>Assigned</dt><dd>{ticket.assigned_to?.name || 'Unassigned'}</dd></div>
        <div><dt>Status</dt><dd>{ticket.status_label}</dd></div>
      </dl>
      <div className="workboard-indicators">
        <span>{ticket.attachment_count} attachments</span>
        <span>{ticket.note_count} notes</span>
        {ticket.due_date && <span>Due {new Date(ticket.due_date).toLocaleDateString()}</span>}
      </div>
      <Link className="team-card-action" to={`/workboard/tickets/${ticket.id}`}>Open Ticket</Link>
    </article>
  );
}

function CreateTicketForm({ board, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', ticket_type: 'general', priority: 'p3', project_id: '', assignee_id: '', requires_qa: false });
  const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault();
    try {
      await createTicket(board.team.id, { ...form, project_id: form.project_id || null, assignee_id: form.assignee_id || null });
      setForm({ title: '', description: '', ticket_type: 'general', priority: 'p3', project_id: '', assignee_id: '', requires_qa: false });
      setOpen(false);
      setError('');
      onCreated();
    } catch (err) {
      setError(err.message || 'Unable to create ticket.');
    }
  }
  if (!board.can_create_ticket) return null;
  return (
    <section className="staff-card workboard-create">
      <button type="button" onClick={() => setOpen(!open)}>{open ? 'Close Ticket Form' : 'Create Ticket'}</button>
      {open && <form onSubmit={submit} className="internal-form-grid">
        <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        <select value={form.ticket_type} onChange={(e) => setForm({ ...form, ticket_type: e.target.value })}>{board.types.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>{board.priorities.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}><option value="">Internal / no project</option>{board.projects.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
        <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}><option value="">Unassigned</option>{board.members.map((item) => <option key={item.id} value={item.id}>{item.name} - {item.discipline_label}</option>)}</select>
        <label className="workboard-checkbox"><input type="checkbox" checked={form.requires_qa} onChange={(e) => setForm({ ...form, requires_qa: e.target.checked })} /> QA Required</label>
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
        <button type="submit">Save Ticket</button>
        {error && <p className="team-form-message is-error">{error}</p>}
      </form>}
    </section>
  );
}

export default function WorkboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '', ticket_type: '', priority: '', assigned_to_me: false, cross_team: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const teamId = searchParams.get('team') || '';

  const requestParams = useMemo(() => ({ team: teamId, ...filters, assigned_to_me: filters.assigned_to_me ? '1' : '', cross_team: filters.cross_team ? '1' : '' }), [teamId, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchWorkboard(requestParams);
      setData(payload);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load Workboard.');
    } finally {
      setLoading(false);
    }
  }, [requestParams]);

  useEffect(() => { load(); }, [load]);

  const board = data?.board;

  function chooseTeam(value) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('team', value); else next.delete('team');
    setSearchParams(next);
  }

  return (
    <div className="staff-shell">
      <ToolSidebar active="workboard" />
      <main className="staff-page internal-page workboard-page">
        <header className="staff-header internal-hero">
          <span>{data?.eyebrow || 'TEAM DELIVERY'}</span>
          <h1>{data?.heading || 'Workboard'}</h1>
          <p>{data?.supporting_text || "Track, assign, test, and complete your team's work."}</p>
        </header>

        {loading ? <section className="staff-card">Loading Workboard...</section> : error ? <section className="staff-card"><p className="staff-empty inline team-error">{error}</p></section> : <>
          <section className="staff-card workboard-toolbar">
            <select aria-label="Choose team" value={board?.team?.id || teamId} onChange={(e) => chooseTeam(e.target.value)}>
              <option value="">Choose a team</option>
              {(data.teams || []).map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
            {board && <>
              <input aria-label="Search tickets" placeholder="Search tickets" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
              <select aria-label="Status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="">All statuses</option>{board.statuses.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
              <select aria-label="Ticket type" value={filters.ticket_type} onChange={(e) => setFilters({ ...filters, ticket_type: e.target.value })}><option value="">All types</option>{board.types.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
              <select aria-label="Priority" value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}><option value="">All priorities</option>{board.priorities.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
              <label><input type="checkbox" checked={filters.assigned_to_me} onChange={(e) => setFilters({ ...filters, assigned_to_me: e.target.checked })} /> Assigned to me</label>
              <label><input type="checkbox" checked={filters.cross_team} onChange={(e) => setFilters({ ...filters, cross_team: e.target.checked })} /> Cross-team</label>
            </>}
          </section>

          {!board ? <section className="staff-card workboard-selector"><h2>Select Team</h2><p className="staff-empty inline">Choose a team board to view.</p></section> : <>
            <section className="workboard-team-heading"><h2>{board.team.name}</h2><span>{board.tickets.length} tickets</span></section>
            <CreateTicketForm board={board} onCreated={load} />
            <section className="workboard-board" aria-label="Workboard columns">
              {board.columns.map((column) => <div className="workboard-column" key={column.status}><header><h2>{column.label}</h2><span>{column.count}</span></header>{column.tickets.length ? column.tickets.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} />) : <p className="staff-empty inline">No tickets.</p>}</div>)}
            </section>
          </>}
        </>}
      </main>
    </div>
  );
}